<template>
  <property-report-card
    title="Historical Rent Trends"
    description="View 7-day moving average rent trends across your comp set."
    :printing-mode="printingMode"
    :is-empty="!unitsFound(filteredProperties)"
  >
    <template #actions>
      <q-btn v-if="!printingMode" flat no-caps @click="exportHistoricalCSV">
        <q-icon name="download" class="q-mr-sm" size="medium" />Download
      </q-btn>
      <select-options
        v-if="!printingMode && grouping.value === 'all'"
        v-model="selectedBuildingAverages"
        label="Property Trends"
        :options="buildingOptions"
        @update:model-value="(val) => $tracker.changedFilter('properties', 'rent_trends', val)"
      />
      <q-select
        v-model="grouping"
        :options="groupingOptions"
        label="Grouping"
        dense
        outlined
        style="width: 150px"
        @update:model-value="(val) => $tracker.changedFilter('group_by', 'rent_trends', val)"
      />
      <q-select
        v-model="metric"
        transition-duration="100"
        :options="metricOptions"
        label="Metric"
        dense
        outlined
        style="width: 150px"
        @update:model-value="(val) => $tracker.changedFilter('metric', 'rent_trends', val)"
      />
    </template>
    <q-separator />
    <q-card-section>
      <property-compare-history-chart
        id="intercom-historical-rent-trends-content"
        class="chart"
        :label="metric.label"
        :history="history"
        :area-annotated="areaAnnotated"
        canvas-style="width: 100%; height: 100%"
        @click="clickedPoint"
      />
      <div v-if="growthValues.length" class="row q-gutter-md q-mt-sm">
        <q-card
          v-for="growth in growthValues"
          :key="growth.label"
          flat
          bordered
          class="q-pa-sm col-2"
          :class="{ 'col-3': $q.screen.lt.lg, 'col-4': $q.screen.lt.md, 'col-12': $q.screen.lt.sm }"
        >
          <div class="text-grey-9 q-mb-xs" style="font-size: 14px">Growth: {{ growth.label }}</div>
          <div
            :class="{ 'text-positive': growth.value > 0, 'text-negative': growth.value <= 0 }"
            class="text-bold"
          >
            {{ formatPercent(growth.value, 1, true, 'N/A') }}
          </div>
        </q-card>
      </div>
    </q-card-section>
    <q-separator v-if="!printingMode" />
    <q-card-section v-if="!printingMode">
      <div v-if="areaAnnotated" class="flex justify-between q-mb-md">
        <div class="text-bold q-mb-md q-my-auto">
          Available Units Between {{ format(areaAnnotated.from, 'M/d/yy') }} and
          {{ format(areaAnnotated.to, 'M/d/yy') }}
          <q-icon name="close" class="q-ml-md cursor-pointer" @click="closePanel" />
        </div>
        <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
          <q-icon name="download" class="q-mr-sm" size="medium" />Download
        </q-btn>
      </div>
      <div
        v-if="!tableUnits.length"
        class="full-width text-center justify-center flex align-center"
      >
        <q-icon name="info" size="18px" color="accent" class="q-mr-sm q-my-auto" />
        Click on a point in time on the chart above to see the units that make it up.
      </div>
      <unit-details-table
        v-else
        :rows="tableUnits"
        :expanded-unit-history="expandedUnitHistory"
        :date-range="dateRange"
      />
    </q-card-section>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { format, eachDayOfInterval, addDays, parseISO } from 'date-fns'
import { exportFile } from 'quasar'

import type { PropertyDetailsResponse, Price, Availability, AvailabilityPeriod } from 'common'
import { getUnitsByFilter, getLatestPricesByFilter } from 'common'
import PropertyCompareHistoryChart, {
  instanceOfLine,
  type Line,
  type Value,
  type RangeArea
} from '@/components/PropertyReports/PropertyCompareHistoryChart.vue'
import UnitDetailsTable, { exportCSV } from './UnitDetailsTable.vue'
import SelectOptions from '@/components/SelectOptions.vue'
import { rollingFunctions, percentileFn, formatPercent, unitTypeColor, unitsFound } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

const colors = [
  '#F80',
  'rgba(255, 193, 7, 1)',
  'rgba(255, 235, 59, 1)',
  'rgba(255, 87, 34, 1)',
  'rgba(3, 169, 244, 1)',
  'rgba(233, 30, 99, 1)',
  'rgba(76, 175, 80, 1)',
  'rgba(156, 39, 176, 1)',
  'rgba(139, 195, 74, 1)',
  'rgba(0, 188, 212, 1)',
  'rgba(22, 191, 195, 1)',
  'rgba(40, 146, 220, 1)'
]

type MetricOption =
  | 'avg_price_psf'
  | 'avg_effective_price_psf'
  | 'avg_price'
  | 'avg_effective_price'
  | 'concession_amount'
  | 'concession_psf'
type MetricSelection = {
  label: string
  value: MetricOption
}

type GroupingOption = 'bed' | 'all'

const computeGrowth = (hist: Line): number | null => {
  let firstNonNullIdx = -1
  let lastNonNullIdx = -1
  for (let i = 0; i < hist.history.length; i++) {
    if (firstNonNullIdx === -1 && hist.history[i].value !== null) {
      firstNonNullIdx = i
    }
    if (lastNonNullIdx === -1 && hist.history[hist.history.length - 1 - i].value !== null) {
      lastNonNullIdx = hist.history.length - 1 - i
    }
    if (firstNonNullIdx !== -1 && lastNonNullIdx !== -1) break
  }

  if (
    firstNonNullIdx !== -1 &&
    lastNonNullIdx !== -1 &&
    hist.history[firstNonNullIdx].value !== 0
  ) {
    return (
      (hist.history[lastNonNullIdx].value - hist.history[firstNonNullIdx].value) /
      hist.history[firstNonNullIdx].value
    )
  }
  return null
}

export default {
  name: 'PropertyCompareRentTrends',
  components: { PropertyReportCard, PropertyCompareHistoryChart, SelectOptions, UnitDetailsTable },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    listingsSelectionValue: {
      type: String as PropType<'all' | 'active' | 'closed'>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      format,
      formatPercent,
      window: 7,
      metric: { label: 'Asking Rent PSF', value: 'avg_price_psf' } as MetricSelection,
      lastAllGroupingMetric: {
        label: 'Asking Rent PSF',
        value: 'avg_price_psf'
      } as MetricSelection,
      metricOptions: [
        { label: 'Asking Rent PSF', value: 'avg_price_psf' },
        { label: 'Effective Rent PSF', value: 'avg_effective_price_psf' },
        { label: 'Concession PSF', value: 'concession_psf' },
        { label: 'Asking Rent', value: 'avg_price' },
        { label: 'Effective Rent', value: 'avg_effective_price' },
        { label: 'Concession $', value: 'concession_amount' }
      ] as { label: string; value: MetricOption }[],
      grouping: {
        label: 'All',
        value: 'all'
      } as {
        label: string
        value: GroupingOption
      },
      groupingOptions: [
        { label: 'Unit Type', value: 'bed' },
        { label: 'All', value: 'all' }
      ] as { label: string; value: GroupingOption }[],
      selectedBuildingAverages: [] as { label: string; value: string }[],
      selectedDate: null as string | null
    }
  },
  computed: {
    areaAnnotated(): {
      from: string
      to: string
    } | null {
      if (this.selectedDate) {
        const halfWindow = Math.floor(this.window / 2)
        return {
          from: format(addDays(parseISO(this.selectedDate), -halfWindow), 'yyyy-MM-dd'),
          to: format(addDays(parseISO(this.selectedDate), halfWindow), 'yyyy-MM-dd')
        }
      }
      return null
    },
    buildingOptions() {
      return this.filteredProperties.map(({ prop }) => ({
        label: prop.building_name || prop.street_address,
        value: prop.id
      }))
    },
    getValueGetter(): (unit: Availability) => (hist: Price) => number | null {
      return (unit: Availability) => {
        switch (this.metric.value) {
          case 'avg_price_psf':
            if (unit.sqft === null) return () => null
            return (hist: Price) => {
              const num = hist.price || hist.min_price || null
              return num !== null ? num / unit.sqft! : null
            }
          case 'avg_effective_price_psf':
            if (unit.sqft === null) return () => null
            return (hist: Price) => {
              const num = hist.effective_price || hist.min_effective_price || null
              return num !== null ? num / unit.sqft! : null
            }
          case 'avg_price':
            return (hist: Price) => hist.price || hist.min_price || null
          case 'avg_effective_price':
            return (hist: Price) => hist.effective_price || hist.min_effective_price || null
          case 'concession_amount':
            return (hist: Price) => {
              const effPrice = hist.effective_price || hist.min_effective_price
              const price = hist.price || hist.min_price
              if (!effPrice || !price) return null
              return price - effPrice
            }
          case 'concession_psf':
            return (hist: Price) => {
              const effPrice = hist.effective_price || hist.min_effective_price
              const price = hist.price || hist.min_price
              if (!effPrice || !price || unit.sqft === null) return null
              const concession = price - effPrice
              return concession / unit.sqft
            }
          default:
            throw new Error('Unknown metric')
        }
      }
    },
    history(): Value[] {
      const fromStr = format(this.dateRange.from, 'yyyy-MM-dd')
      const toStr = format(this.dateRange.to, 'yyyy-MM-dd')

      // We pre-compute the labels for the chart, and we can then iterate over each day at low cost
      const labels = eachDayOfInterval({
        start: this.dateRange.from,
        end: this.dateRange.to
      }).map((date) => format(date, 'yyyy-MM-dd'))
      const dateStrings = new Map(labels.map((label, index) => [label, index]))

      const valueGetterFn = this.getValueGetter

      if (this.grouping.value === 'all') {
        // for each property, we filter the units and then for each unit, we add the price to the map
        const marketDate: number[][][] = Array.from(
          { length: this.filteredProperties.length },
          () => Array.from({ length: labels.length }, () => [])
        )

        for (let propIdx = 0; propIdx < this.filteredProperties.length; propIdx++) {
          // create a set of (unit_id, period_id), to make sure we don't double count
          const alreadySeen = new Set()
          for (const { unit } of this.filteredProperties[propIdx].units) {
            const getterFn = valueGetterFn(unit)

            if (alreadySeen.has(unit.id)) {
              continue
            }
            alreadySeen.add(unit.id)
            for (const history of unit.history) {
              if (!(history.from_date > toStr || history.to_date < fromStr)) {
                const data = getterFn(history)
                if (data === null) {
                  continue
                }

                const firstDayStr = history.from_date > fromStr ? history.from_date : fromStr
                const lastDayStr = history.to_date < toStr ? history.to_date : toStr

                const idxFrom = dateStrings.get(firstDayStr)!
                const idxTo = dateStrings.get(lastDayStr)!

                for (let dayIdx = idxFrom; dayIdx <= idxTo; dayIdx++) {
                  marketDate[propIdx][dayIdx].push(data)
                }
              }
            }
          }
        }

        // we compute the overall market distribution
        const allArray: number[][] = Array.from({ length: labels.length }, () => [])

        for (const propData of marketDate) {
          for (let dayIdx = 0; dayIdx < propData.length; dayIdx++) {
            allArray[dayIdx].push(...propData[dayIdx])
          }
        }

        const aggregatedValues = rollingFunctions(
          allArray,
          this.window,
          percentileFn([0.05, 0.1, 0.25, 0.5, 0.75, 0.9, 0.95])
        )

        // we compute the avg for selected buildings
        const selectedBuildingAverages = this.selectedBuildingAverages
          .map((s, buildingIdx) => {
            const idx = this.filteredProperties.findIndex(({ prop }) => prop.id === s.value)
            if (idx < 0) {
              return null
            }
            const avgData = rollingFunctions(marketDate[idx], this.window, percentileFn([0.5]))
            return {
              label:
                (this.filteredProperties[idx].prop.building_name ||
                  this.filteredProperties[idx].prop.street_address) + ' (Median)',
              history: labels.map((label, idx) => {
                return {
                  date: label,
                  value: avgData[idx][0]
                }
              }),
              color: colors[buildingIdx % colors.length]
            }
          })
          .filter((item): item is Line => item !== null)

        return [
          ...selectedBuildingAverages,
          {
            label: 'Market (Median)',
            history: labels.map((label, idx) => {
              return {
                date: label,
                value: aggregatedValues[idx][3]
              }
            }),
            color: '#6365f1'
          } as Line,
          {
            legendLabel: '50% of Units',
            labels: ['25th Perc', '75th Perc'],
            history: labels.map((label, idx) => {
              return {
                date: label,
                value: [aggregatedValues[idx][2], aggregatedValues[idx][4]]
              }
            }),
            color: 'rgba(183, 184, 248, 0.5)'
          } as RangeArea,
          {
            legendLabel: '80% of Units',
            labels: ['10th Perc', '90th Perc'],
            history: labels.map((label, idx) => {
              return {
                date: label,
                value: [aggregatedValues[idx][1], aggregatedValues[idx][5]]
              }
            }),
            color: 'rgba(211, 212, 250, 0.5)'
          } as RangeArea,
          {
            legendLabel: '90% of Units',
            labels: ['5th Perc', '95th Perc'],
            history: labels.map((label, idx) => {
              return {
                date: label,
                value: [aggregatedValues[idx][0], aggregatedValues[idx][6]]
              }
            }),
            color: 'rgba(233,233,252, 0.5)'
          } as RangeArea
        ]
      }

      // For bed/bath grouping
      const bedBathGroups = new Map<number, number[][][]>()

      // First pass: collect all unique bed/bath combinations and initialize arrays
      for (const { units } of this.filteredProperties) {
        for (const { unit } of units) {
          if (unit.bed === null) continue
          if (!bedBathGroups.has(unit.bed)) {
            bedBathGroups.set(
              unit.bed,
              Array.from({ length: this.filteredProperties.length }, () =>
                Array.from({ length: labels.length }, () => [])
              )
            )
          }
        }
      }

      // Second pass: populate data for each bed/bath group
      for (let propIdx = 0; propIdx < this.filteredProperties.length; propIdx++) {
        const alreadySeen = new Set()
        for (const { unit } of this.filteredProperties[propIdx].units) {
          if (alreadySeen.has(unit.id) || unit.bed === null) continue
          alreadySeen.add(unit.id)

          const key = unit.bed
          const marketData = bedBathGroups.get(key)!
          const getterFn = valueGetterFn(unit)

          for (const history of unit.history) {
            if (!(history.from_date > toStr || history.to_date < fromStr)) {
              const data = getterFn(history)
              if (data === null) continue

              const firstDayStr = history.from_date > fromStr ? history.from_date : fromStr
              const lastDayStr = history.to_date < toStr ? history.to_date : toStr
              const idxFrom = dateStrings.get(firstDayStr)!
              const idxTo = dateStrings.get(lastDayStr)!

              for (let dayIdx = idxFrom; dayIdx <= idxTo; dayIdx++) {
                marketData[propIdx][dayIdx].push(data)
              }
            }
          }
        }
      }

      const result: Value[] = []

      // Add ranges for each bed/bath group
      const sortedBedBathEntries = Array.from(bedBathGroups.entries()).sort(([a], [b]) => a - b)
      for (const [key, marketData] of sortedBedBathEntries) {
        const allArray: number[][] = Array.from({ length: labels.length }, () => [])

        for (const propData of marketData) {
          for (let dayIdx = 0; dayIdx < propData.length; dayIdx++) {
            allArray[dayIdx].push(...propData[dayIdx])
          }
        }

        const aggregatedValues = rollingFunctions(allArray, this.window, percentileFn([0.5]))

        result.push({
          label: `${key}BR`,
          history: labels.map((label, idx) => ({
            date: label,
            value: aggregatedValues[idx][0]
          })),
          color: unitTypeColor(key)
        } as Line)
      }

      return result
    },
    growthValues() {
      // find all lines
      const lines = this.history.filter(instanceOfLine)

      const growthValues = []
      for (const hist of lines) {
        const growth = computeGrowth(hist)
        if (growth !== null) {
          growthValues.push({
            label: hist.label,
            value: growth
          })
        }
      }

      return growthValues
    },
    tableUnits(): {
      unit: Availability
      lastHistory: Price
      prop: PropertyDetailsResponse
      period: AvailabilityPeriod
    }[] {
      const date = this.selectedDate
      if (date === null) {
        return []
      }

      const dateMin = addDays(parseISO(date), -this.window)
      const dateMax = addDays(parseISO(date), this.window)

      return this.filteredProperties.flatMap(({ prop, units }) => {
        const seen = new Map()
        const uniqueUnits = units.reduce((acc: Availability[], obj) => {
          if (!seen.has(obj.unit.id)) {
            seen.set(obj.unit.id, true)
            acc.push(obj.unit)
          }
          return acc
        }, [])
        return getUnitsByFilter(
          prop,
          getLatestPricesByFilter({ building_availability: uniqueUnits }, dateMin, dateMax),
          dateMax,
          this.listingsSelectionValue
        ).flatMap((_) => {
          return {
            unit: _.unit,
            lastHistory: _.lastHistory,
            prop,
            period: _.period
          }
        })
      })
    }
  },
  watch: {
    dateRange() {
      this.selectedDate = null
    },
    metric(newMetric: MetricSelection) {
      if (this.grouping.value === 'all') {
        this.lastAllGroupingMetric = { ...newMetric }
      }
    },
    grouping(newGrouping: { value: string; label: string }) {
      if (newGrouping.value === 'bed' && this.metric.value !== 'avg_price') {
        this.metric = {
          label: 'Asking Rent',
          value: 'avg_price'
        }
      } else if (newGrouping.value === 'all') {
        this.metric = { ...this.lastAllGroupingMetric }
      }
    }
  },
  created() {
    if (this.subjectDetail) {
      this.selectedBuildingAverages = [
        {
          label: this.subjectDetail.building_name || this.subjectDetail.street_address,
          value: this.subjectDetail.id
        }
      ]
    }
  },
  methods: {
    unitsFound,
    exportCSV() {
      exportCSV(this.tableUnits, 'rent-trend-unit-details', this.dateRange)
      this.$tracker.exported('rent_trend_unit_details')
    },
    clickedPoint(event: { position: number; dataset: number }) {
      if (!this.printingMode) {
        this.$tracker.clickedOnGraph('rent_trend')
        this.selectedDate = this.history[0].history[event.position].date
      }
    },
    closePanel() {
      this.selectedDate = null
    },
    exportHistoricalCSV() {
      const exportDecimal = (num: number) => Math.round(num * 100) / 100
      if (this.history.length) {
        const lookup = {} as { [date: string]: number[] }
        const labels: string[] = []

        for (const hist of this.history.filter(instanceOfLine)) {
          labels.push(hist.label)
          for (const h of hist.history) {
            if (!lookup[h.date]) {
              lookup[h.date] = []
            }
            lookup[h.date].push(exportDecimal(h.value))
          }
        }
        const keys = Object.keys(lookup)
        keys.sort()
        const rows = keys.map((key) => {
          return [key, ...lookup[key]]
        })
        const today = new Date().toISOString().split('T')[0]
        exportFile(
          'hello-data-rent-trend-' + today + '.csv',
          `Date,${labels.join(',')}\n` + rows.map((row) => Object.values(row).join(',')).join('\n')
        )
        this.$tracker.exported('rent_trend')
      }
    }
  }
}
</script>
<style scoped>
.chart {
  height: 350px;
  width: 100%;
}
.text-positive {
  color: #4caf50;
}
.text-negative {
  color: #f44336;
}
</style>
